import type { OutgoingIntakeReminderSchedule } from "@/api/types/outgoingIntake";

export const EXPIRY_DAYS = [7, 14, 30, 60, 90] as const;

export const REMINDER_PRESETS: {
  id: string;
  label: string;
  schedule: OutgoingIntakeReminderSchedule | null;
}[] = [
  {
    id: "standard",
    label: "Day 3, day 7, before expiry",
    schedule: { days_after_send: [3, 7], days_before_expiry: 2 }
  },
  { id: "light", label: "Day 3 only", schedule: { days_after_send: [3] } },
  {
    id: "expiry",
    label: "Only before expiry",
    schedule: { days_after_send: [], days_before_expiry: 2 }
  },
  { id: "off", label: "Off", schedule: null }
];

export const CUSTOM_PRESET_ID = "custom";

/** Day chips the custom cadence offers (the server takes any day from 1 to 60). */
export const DAY_CHOICES = [1, 2, 3, 5, 7, 10, 14, 21, 30] as const;
/** The server takes 1 to 30 days before expiry. */
export const BEFORE_EXPIRY_CHOICES = [1, 2, 3, 5, 7] as const;

/** Where Custom… starts when reminders were off. */
export const STARTER_CUSTOM: OutgoingIntakeReminderSchedule = {
  days_after_send: [3],
  days_before_expiry: null
};

export const toggleDay = (
  schedule: OutgoingIntakeReminderSchedule,
  day: number
): OutgoingIntakeReminderSchedule => ({
  ...schedule,
  days_after_send: schedule.days_after_send.includes(day)
    ? schedule.days_after_send.filter((d) => d !== day)
    : [...schedule.days_after_send, day].sort((a, b) => a - b)
});

export const withBeforeExpiry = (
  schedule: OutgoingIntakeReminderSchedule,
  days: number | null
): OutgoingIntakeReminderSchedule => ({
  ...schedule,
  days_before_expiry: days
});

const same = (
  a: OutgoingIntakeReminderSchedule | null,
  b: OutgoingIntakeReminderSchedule | null
) =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.days_after_send.join(",") === b.days_after_send.join(",") &&
    (a.days_before_expiry ?? null) === (b.days_before_expiry ?? null));

/** The preset a schedule matches, or "custom" (set elsewhere, e.g. by the assistant). */
export const reminderPresetId = (
  schedule: OutgoingIntakeReminderSchedule | null | undefined
): string =>
  REMINDER_PRESETS.find((p) => same(p.schedule, schedule ?? null))?.id ??
  CUSTOM_PRESET_ID;

/** "Day 2, day 5, 1 day before expiry" for a custom schedule. */
export const describeReminders = (
  schedule: OutgoingIntakeReminderSchedule | null | undefined
): string => {
  if (!schedule) return "Off";
  const parts = schedule.days_after_send.map((d) => `day ${d}`);
  if (schedule.days_before_expiry)
    parts.push(
      `${schedule.days_before_expiry} ${
        schedule.days_before_expiry === 1 ? "day" : "days"
      } before expiry`
    );
  const text = parts.join(", ");
  return text ? text[0].toUpperCase() + text.slice(1) : "Off";
};

/** A preset's label, or the schedule spelled out. */
export const cadenceLabel = (
  schedule: OutgoingIntakeReminderSchedule | null | undefined
): string =>
  REMINDER_PRESETS.find((p) => same(p.schedule, schedule ?? null))?.label ??
  describeReminders(schedule);
